import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { dailyReportBXService } from 'BoostXpressAdmin/Services/dailyReportBXService'
import { DailyReportDetail, ReportStatus } from 'BoostXpressAdmin/Model/dailyReportXModel'
import DailyReportContentsSections from './DailyReportContentsSections'
import './DailyReportDetails.scss'

type ReportRouteState = { station?: string; manager?: string }

const DailyReportDetails: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { id } = useParams<{ id: string }>()
  const routeState = (location.state as ReportRouteState | null) ?? {}
  const [report, setReport] = useState<DailyReportDetail | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState('')

  useEffect(() => {
    const fetchReport = async () => {
      if (!id) {
        setReport(null)
        setLoadError('The report ID is missing.')
        setIsLoading(false)
        return
      }

      try {
        setIsLoading(true)
        setLoadError('')
        setReport(await dailyReportBXService.getReviewById(id, routeState))
      } catch {
        setReport(null)
        setLoadError('Failed to load this daily report. Please try again.')
      } finally {
        setIsLoading(false)
      }
    }

    void fetchReport()
  }, [id, routeState.manager, routeState.station])

  const renderStatusBadge = (status: ReportStatus) => {
    switch (status) {
      case 'Pending Associate Review':
        return (
          <span className='bx-drd-status bx-drd-status--pending'>Pending Associate Review</span>
        )
      case 'Finance Review':
        return <span className='bx-drd-status bx-drd-status--review'>Finance Review</span>
      case 'Published':
        return <span className='bx-drd-status bx-drd-status--approved'>Published</span>
      case 'Correction Required':
        return <span className='bx-drd-status bx-drd-status--returned'>Correction Required</span>
      default:
        return <span className='bx-drd-status'>{status}</span>
    }
  }

  if (isLoading) {
    return (
      <div className='bx-daily-report-details-page'>
        <div className='bx-drd-loading' role='status'>
          Loading report details...
        </div>
      </div>
    )
  }

  if (!report) {
    return (
      <div className='bx-daily-report-details-page'>
        <div className='bx-drd-loading' role='alert'>
          {loadError || 'Report not found.'}
        </div>
        <button
          type='button'
          className='bx-drd-breadcrumb__link'
          onClick={() => navigate('/boostxpress/daily-reports')}
        >
          Back to Daily Reports
        </button>
      </div>
    )
  }

  return (
    <div className='bx-daily-report-details-page'>
      <div className='bx-drd-breadcrumb'>
        <button
          type='button'
          className='bx-drd-breadcrumb__link'
          onClick={() => navigate('/boostxpress/daily-reports')}
        >
          &lsaquo; Daily Reports
        </button>
        <span className='bx-drd-breadcrumb__sep'>&rsaquo;</span>
        <span className='bx-drd-breadcrumb__current'>Report Review</span>
      </div>

      <div className='bx-drd-banner'>
        <div className='bx-drd-banner__info'>
          <h1 className='bx-drd-banner__station'>{report.station}</h1>
          <div className='bx-drd-banner__meta'>
            Submitted by {report.manager} · {report.submittedDateFormatted}
          </div>
        </div>
        <div className='bx-drd-banner__badge'>{renderStatusBadge(report.status)}</div>
      </div>

      <DailyReportContentsSections reportResourceId={report.id} />

      <section className='bx-drd-section'>
        <h3 className='bx-drd-section-title'>Manager notes</h3>
        <div className='bx-drd-card'>
          <p className='bx-drd-notes-text'>
            {report.managerNotes || 'No notes submitted by the station manager.'}
          </p>
        </div>
      </section>
    </div>
  )
}

export default DailyReportDetails
